import { useCallback, useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Toolbar, SegmentedFilter } from "@/components/ui/Toolbar";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Icon } from "@/components/ui/Icon";
import { ApiError } from "@/lib/api/errors";
import { printFile } from "@/lib/api/files";
import { useAction, useItem, useList } from "@/lib/api/queries";
import { request } from "@/lib/api/client";
import { useSession } from "@/lib/auth/useSession";
import { PERM } from "@/lib/auth/rbac";
import { qty, relative, text } from "@/lib/format";
import { useReport } from "@/lib/api/reports";
import "./skus.css";

/**
 * Tovar kartalari (master-data).
 *
 * Artikul, nom va birlik 1C dan keladi va bu yerda o'zgartirilmaydi.
 * WMS o'zi boyitadigan maydonlar — VGH, ИКПУ/ТН ВЭД, shtrix-kodlar,
 * mo'rtlik — shu ekranda: VGH siz tovar avtomatik joylashtirishga
 * tushmaydi (P04-001), shuning uchun «VGH yo'q» filtri bor.
 *
 * Yorliq bitta bosishda printerga ketadi (LBL-01): tizimning
 * chop etish dialogi ochiladi, fayl saqlanmaydi.
 */

interface Dimensions {
  length_cm?: string;
  width_cm?: string;
  height_cm?: string;
  weight_kg?: string;
}

interface Sku {
  id: string;
  /** 1C KODI (Номенклатура.Код, «GD-00019036»). */
  article: string;
  /** Haqiqiy artikul (1C «Артикул», «440229») — OData dan. */
  vendor_article?: string;
  name: string;
  name_ru?: string;
  base_uom?: string;
  /** Qadoq shakli («2x6») — tovar nomi oxiridan; 1C da alohida maydon yo'q. */
  pack_format?: string;
  /** 1C guruh KODI («GD-000431») va uning NOMI («Вазы»). */
  product_group?: string;
  product_group_name?: string;
  brand?: string;
  photo_url?: string;
  abc_class?: string;
  xyz_class?: string;
  dimensions?: Dimensions;
  has_complete_vgh?: boolean;
  ikpu_code?: string;
  tnved_code?: string;
  fragile?: boolean;
  no_stack?: boolean;
  /** Hisob rejimi — 1C niki, WMS o'zgartirmaydi. */
  tracks_lot?: boolean;
  tracks_serial?: boolean;
  shelf_life_days?: number;
  barcodes?: string[];
  external_id?: string;
  is_active?: boolean;
  version: number;
  updated_at?: string;
}

interface Package {
  id: string;
  code: string;
  name?: string;
  units_per_pack: string;
  is_base?: boolean;
  barcodes?: string[];
}

type Tab = "all" | "missing" | "inactive";

/**
 * Ro'yxat SAHIFALANMAYDI: butun katalog (~57 000 tovar) bir marta
 * yuklanadi, qidiruv va ustun filtrlari brauzerda hammasi ustida
 * ishlaydi, jadval esa faqat ko'rinadigan qatorlarni chizadi.
 * Sahifalangan ro'yxatda filtr faqat ochiq sahifaga qo'llanardi —
 * «filtrda hammasi ko'rinmayapti».
 */

/** Qidiruv: 1C kodi, artikul, nom, brend, guruh yoki shtrix-kod. */
function matches(sku: Sku, needle: string): boolean {
  if (!needle) return true;
  const hay = [sku.article, sku.vendor_article, sku.name, sku.brand, sku.product_group_name, sku.product_group]
    .filter(Boolean)
    .join("\n")
    .toLowerCase();
  return hay.includes(needle) || (sku.barcodes ?? []).some((code) => code === needle);
}

/** Excel uchun CSV: `;` ajratuvchi va BOM — rus/o'zbek Excel shunday ochadi. */
function exportCsv(rows: Sku[]) {
  const cell = (value: unknown) => `"${String(value ?? "").replace(/"/g, '""')}"`;
  // Uzun raqamni Excel «4,6E+12» qilib yuborardi — matn sifatida beriladi.
  const asText = (value?: string) => (value ? `="${value.replace(/"/g, '""')}"` : "");
  const header = ["1C kodi", "Artikul", "Nomi", "Guruh", "Brend", "Birlik", "Qadoq", "ABC", "Uzunlik sm", "Eni sm",
    "Bo'yi sm", "Vazn kg", "Shtrix-kodlar", "Mo'rt", "Ustiga qo'yilmaydi"];
  const lines = [header.map(cell).join(";")];
  for (const sku of rows) {
    lines.push([
      cell(sku.article), cell(sku.vendor_article), cell(sku.name), cell(sku.product_group_name || sku.product_group),
      cell(sku.brand), cell(sku.base_uom), cell(sku.pack_format), cell(sku.abc_class),
      cell(sku.dimensions?.length_cm), cell(sku.dimensions?.width_cm), cell(sku.dimensions?.height_cm),
      cell(sku.dimensions?.weight_kg),
      (sku.barcodes ?? []).length ? asText((sku.barcodes ?? []).join(", ")) : '""',
      cell(sku.fragile ? "ha" : ""), cell(sku.no_stack ? "ha" : ""),
    ].join(";"));
  }
  const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `tovarlar-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const TAB_QUERY: Record<Tab, Record<string, string | boolean | undefined>> = {
  all: { is_active: true },
  missing: { is_active: true, missing_vgh: true },
  inactive: { is_active: false },
};

interface Form {
  name_ru: string;
  product_group: string;
  length_cm: string;
  width_cm: string;
  height_cm: string;
  weight_kg: string;
  ikpu_code: string;
  tnved_code: string;
  barcodes: string;
  fragile: boolean;
  no_stack: boolean;
}

function formOf(sku: Sku): Form {
  return {
    name_ru: sku.name_ru ?? "",
    product_group: sku.product_group ?? "",
    length_cm: sku.dimensions?.length_cm ?? "",
    width_cm: sku.dimensions?.width_cm ?? "",
    height_cm: sku.dimensions?.height_cm ?? "",
    weight_kg: sku.dimensions?.weight_kg ?? "",
    ikpu_code: sku.ikpu_code ?? "",
    tnved_code: sku.tnved_code ?? "",
    barcodes: (sku.barcodes ?? []).join(", "),
    fragile: sku.fragile ?? false,
    no_stack: sku.no_stack ?? false,
  };
}

/**
 * Faqat O'ZGARGAN maydonlar yuboriladi: tegilmagan maydon (masalan 1C
 * bergan guruh) bo'sh qiymat bilan ustidan yozilmaydi.
 */
function changesOf(before: Form, after: Form): Record<string, unknown> {
  const changes: Record<string, unknown> = {};
  const plain = ["name_ru", "product_group", "ikpu_code", "tnved_code"] as const;
  for (const key of plain) {
    if (after[key].trim() !== before[key]) changes[key] = after[key].trim();
  }
  const sizes = ["length_cm", "width_cm", "height_cm", "weight_kg"] as const;
  if (sizes.some((key) => after[key].trim() !== before[key])) {
    changes.dimensions = Object.fromEntries(sizes.map((key) => [key, after[key].trim() || undefined]));
  }
  const codes = (value: string) => value.split(/[\s,;]+/).filter(Boolean);
  if (codes(after.barcodes).join(",") !== codes(before.barcodes).join(",")) changes.barcodes = codes(after.barcodes);
  if (after.fragile !== before.fragile) changes.fragile = after.fragile;
  if (after.no_stack !== before.no_stack) changes.no_stack = after.no_stack;
  return changes;
}

export function Skus() {
  const session = useSession();
  const canWrite = session.can(PERM.masterDataWrite);
  const [tab, setTab] = useState<Tab>("all");
  const [search, setSearch] = useState("");
  const [needle, setNeedle] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [conflict, setConflict] = useState(false);
  const [labels, setLabels] = useState<{ busy: boolean; error?: string }>({ busy: false });

  // Qidiruv BRAUZERDA, butun katalog ustida (yozish tugagach).
  useEffect(() => {
    const timer = window.setTimeout(() => setNeedle(search.trim().toLowerCase()), 200);
    return () => window.clearTimeout(timer);
  }, [search]);

  const query = useList<Sku>("masterData", "/api/v1/skus/catalog", TAB_QUERY[tab]);
  const [visibleRows, setVisibleRows] = useState<Sku[]>([]);
  const onVisibleRows = useCallback((next: Sku[]) => setVisibleRows(next), []);
  const card = useItem<Sku>("masterData", `/api/v1/skus/${editing ?? ""}`, { enabled: editing !== null });
  const packages = useList<Package>("masterData", `/api/v1/skus/${editing ?? ""}/packages`, {}, {
    enabled: editing !== null,
  });

  // Karta kelganda (yoki 409 dan keyin yangilanganda) forma serverdagi
  // qiymatlar bilan to'ldiriladi — eski qiymat ustidan yozilmaydi.
  useEffect(() => {
    if (card.data) setForm(formOf(card.data));
  }, [card.data]);

  const save = useAction<Sku, { sku: Sku; changes: Record<string, unknown> }>("masterData", ({ sku, changes }) => ({
    path: `/api/v1/skus/${sku.id}`,
    method: "PATCH",
    body: { version: sku.version, ...changes },
  }));

  const all = query.data?.items;
  const rows = useMemo(() => (all ?? []).filter((sku) => matches(sku, needle)), [all, needle]);
  const allSelected = visibleRows.length > 0 && visibleRows.every((row) => selected.has(row.id));
  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const columns: Array<Column<Sku>> = [
    {
      key: "pick",
      header: "",
      width: "36px",
      render: (row) => (
        <input
          type="checkbox"
          aria-label={`${row.article} tanlash`}
          checked={selected.has(row.id)}
          onChange={() => toggle(row.id)}
        />
      ),
    },
    {
      key: "vendor_article",
      header: "Artikul",
      value: (row) => row.vendor_article ?? "",
      render: (row) => <span className="mono" style={{ whiteSpace: "nowrap" }}>{text(row.vendor_article)}</span>,
    },
    {
      key: "article",
      header: "1C kodi",
      value: (row) => row.article,
      render: (row) => <span className="mono" style={{ whiteSpace: "nowrap" }}>{row.article}</span>,
    },
    {
      key: "name",
      header: "Nomi",
      value: (row) => row.name,
      // Brend nom OSTIDA: alohida ustun jadvalni kengaytirardi,
      // holbuki brend nomning bir qismi kabi o'qiladi.
      render: (row) => (
        <>
          {row.name}
          {row.brand && <div className="sku-sub">{row.brand}</div>}
        </>
      ),
    },
    {
      key: "group",
      // Kod emas, NOM: «GD-000431» xodimga hech nima demaydi.
      // Nom kelmagan bo'lsa kod ko'rinadi — bu «1C nom
      // yubormadi» degan ochiq belgi, jim bo'sh katak emas.
      header: "Guruh",
      value: (row) => row.product_group_name || row.product_group || "",
      render: (row) => (
        <span title={row.product_group ? `1C kodi: ${row.product_group}` : undefined}>
          {text(row.product_group_name || row.product_group)}
        </span>
      ),
    },
    { key: "uom", header: "Birlik", value: (row) => row.base_uom ?? "", render: (row) => text(row.base_uom) },
    {
      key: "pack",
      header: "Qadoq",
      value: (row) => row.pack_format ?? "",
      render: (row) => <span className="mono">{text(row.pack_format)}</span>,
    },
    {
      key: "class",
      header: "ABC/XYZ",
      value: (row) => (row.abc_class || row.xyz_class ? `${row.abc_class ?? "–"}${row.xyz_class ?? ""}` : "—"),
      render: (row) => (row.abc_class || row.xyz_class ? `${row.abc_class ?? "–"}${row.xyz_class ?? ""}` : "—"),
    },
    {
      key: "vgh",
      header: "VGH",
      value: (row) => (row.has_complete_vgh ? "bor" : "VGH yo'q"),
      render: (row) =>
        row.has_complete_vgh ? (
          <span className="mono" style={{ whiteSpace: "nowrap" }}>
            {qty(row.dimensions?.length_cm)}×{qty(row.dimensions?.width_cm)}×{qty(row.dimensions?.height_cm)} sm ·{" "}
            {qty(row.dimensions?.weight_kg)} kg
          </span>
        ) : (
          <StatusBadge value="VGH yo'q" tone="warning" />
        ),
    },
    {
      key: "barcodes",
      header: "Shtrix-kod",
      value: (row) => (row.barcodes?.length ? "bor" : "—"),
      render: (row) =>
        row.barcodes?.length ? (
          // Ro'yxatda ikkitasi: uchinchisidan boshlab ustun
          // kengayib, qolgan ustunlarni siqib qo'yardi. To'liq
          // ro'yxat kartada.
          <span className="mono" title={row.barcodes.join("\n")}>
            {row.barcodes.slice(0, 2).join(", ")}
            {row.barcodes.length > 2 ? ` +${row.barcodes.length - 2}` : ""}
          </span>
        ) : (
          "—"
        ),
    },
    {
      key: "flags",
      header: "Pallet",
      value: (row) => [row.fragile && "mo'rt", row.no_stack && "ustiga yo'q"].filter(Boolean).join(", ") || "—",
      render: (row) => (
        <span style={{ display: "inline-flex", gap: 4 }}>
          {row.fragile && <StatusBadge value="mo'rt" tone="info" />}
          {row.no_stack && <StatusBadge value="ustiga yo'q" tone="info" />}
          {!row.fragile && !row.no_stack && "—"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (row) => (
        <button
          type="button"
          className="btn btn-tonal btn-sm"
          onClick={() => {
            setConflict(false);
            setForm(null);
            setEditing(row.id);
          }}
        >
          <Icon name={canWrite ? "settings" : "search"} size={14} />
          {canWrite ? "Karta" : "Ko'rish"}
        </button>
      ),
    },
  ];

  const printLabels = () => {
    setLabels({ busy: true });
    const items = [...selected].map((sku_id) => ({ sku_id, copies: 1 }));
    printFile("masterData", "/api/v1/skus/labels", `yorliqlar-${items.length}.pdf`, { items })
      .then(() => setLabels({ busy: false }))
      .catch((cause: unknown) => setLabels({ busy: false, error: cause instanceof Error ? cause.message : String(cause) }));
  };

  const set = (key: keyof Form) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((current) =>
      current ? { ...current, [key]: event.target.type === "checkbox" ? event.target.checked : event.target.value } : current,
    );
  const field = (key: keyof Form, label: string, hint?: string) => (
    <label className="field" style={{ minWidth: 0 }}>
      <span className="field-label">{label}</span>
      <input
        className="input"
        style={{ width: "100%" }}
        value={String(form?.[key] ?? "")}
        onChange={set(key)}
        disabled={!canWrite}
      />
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );

  const sku = card.data;
  const saveError = conflict
    ? "Kartani boshqa foydalanuvchi o'zgartirgan — yangi qiymatlar ko'rsatildi. Tekshirib, qayta saqlang."
    : save.error ?? card.error;

  return (
    <>
      <PageHeader
        title="Tovarlar"
        hint="Artikul, nom va birlik 1C dan. WMS to'ldiradi: VGH, ИКПУ, shtrix-kod, mo'rtlik. VGH siz tovar avtomatik joylashtirilmaydi."
      />

      <Toolbar
        search={search}
        onSearch={setSearch}
        placeholder="Artikul, 1C kodi, nom yoki shtrix-kod…"
        onRefresh={() => void query.refetch()}
        refreshing={query.isFetching}
      >
        <SegmentedFilter
          value={tab}
          onChange={(value) => {
            setTab(value);
            setSelected(new Set());
          }}
          options={[
            { value: "all", label: "Faol" },
            { value: "missing", label: "VGH yo'q" },
            { value: "inactive", label: "Nofaol" },
          ]}
        />
        <button
          type="button"
          className="btn btn-tonal"
          disabled={selected.size === 0 || labels.busy}
          onClick={printLabels}
          title="LBL-01: 70×37 mm, A4 da 24 ta. Chop etish dialogi ochiladi."
        >
          <Icon name="printer" size={16} />
          {labels.busy ? "Tayyorlanmoqda…" : `Yorliq chop etish${selected.size ? ` · ${selected.size}` : ""}`}
        </button>
        <button
          type="button"
          className="btn btn-tonal"
          disabled={visibleRows.length === 0}
          onClick={() => exportCsv(visibleRows)}
          title="Qidiruv va ustun filtridan keyingi BUTUN ro'yxat — Excel da ochiladi"
        >
          <Icon name="download" size={16} />
          Excel · {visibleRows.length.toLocaleString("ru-RU")}
        </button>
      </Toolbar>
      {labels.error && (
        <div className="notice" role="alert" style={{ marginBottom: 12 }}>
          <Icon name="alert" size={16} />
          <span>{labels.error}</span>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        isLoading={query.isLoading}
        error={query.error}
        onRetry={() => void query.refetch()}
        emptyTitle="Tovar topilmadi"
        emptyHint={tab === "missing" ? "Barcha faol tovarlarda VGH to'liq." : "Qidiruvni o'zgartirib ko'ring."}
        virtualize
        onVisibleRows={onVisibleRows}
        footer={
          <span style={{ display: "inline-flex", gap: 12, alignItems: "center", whiteSpace: "nowrap" }}>
            <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={allSelected}
                onChange={() => setSelected(allSelected ? new Set() : new Set(visibleRows.map((row) => row.id)))}
              />
              Ko'rinayotganlarning hammasini tanlash
            </label>
            <span>
              {selected.size > 0 ? `${selected.size} tanlandi · ` : ""}
              {visibleRows.length.toLocaleString("ru-RU")} / {(all?.length ?? 0).toLocaleString("ru-RU")} ta tovar
            </span>
          </span>
        }
      />

      <ConfirmDialog
        open={editing !== null}
        title={sku ? `${sku.vendor_article ? `${sku.vendor_article} · ` : ""}${sku.name} · 1C: ${sku.article}` : "Tovar kartasi"}
        description={
          sku ? (
            <>
              {text(sku.base_uom)}
              {sku.pack_format ? ` · qadoq ${sku.pack_format}` : ""} · 1C: <span className="mono">{text(sku.external_id)}</span> · yangilandi{" "}
              {relative(sku.updated_at)}
            </>
          ) : null
        }
        confirmLabel={canWrite ? "Saqlash" : "Yopish"}
        busy={save.isPending}
        error={saveError}
        confirmDisabled={!sku || !form}
        card
        extra={
          !form || !sku ? (
            <div className="skeleton" style={{ height: 240 }} />
          ) : (
            <div style={{ display: "grid", gap: 16 }}>
              {/* 1C MA'LUMOTI alohida va O'ZGARTIRILMAYDI: xodim
                  qaysi maydon 1C niki, qaysi biri WMS niki
                  ekanini bilmasa, 1C ustidan yozmoqchi bo'lib
                  vaqt yo'qotardi. */}
              <div className="sku-section-title">1C dan keladi</div>
              <OneCFacts sku={sku} />

              <div className="sku-section-title">
                WMS to'ldiradi
                <span className="sku-sub">bu maydonlarni 1C bosmaydi</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 8 }}>
                {field("length_cm", "Uzunlik, sm")}
                {field("width_cm", "Eni, sm")}
                {field("height_cm", "Balandlik, sm")}
                {field("weight_kg", "Vazn, kg")}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {field("ikpu_code", "ИКПУ")}
                {field("tnved_code", "ТН ВЭД")}
              </div>
              {/* Kodlar RO'YXAT bo'lib ko'rinadi: bitta uzun
                  satrda qaysi biri alohida kod ekani ajralmasdi.
                  Tahrir esa o'sha satrda qoladi — vergul bilan
                  yozish qo'shish va o'chirishning eng qisqa
                  yo'li. */}
              <label className="field" style={{ minWidth: 0 }}>
                <span className="field-label">
                  Shtrix-kodlar
                  {(sku.barcodes?.length ?? 0) > 0 && (
                    <span className="sku-sub">{sku.barcodes?.length} ta</span>
                  )}
                </span>
                {(sku.barcodes?.length ?? 0) > 0 && (
                  <span className="sku-codes">
                    {sku.barcodes?.map((code) => (
                      <span className="sku-code mono" key={code}>
                        {code}
                      </span>
                    ))}
                  </span>
                )}
                <input
                  className="input"
                  style={{ width: "100%" }}
                  value={form.barcodes}
                  onChange={set("barcodes")}
                  disabled={!canWrite}
                />
                <span className="field-hint">
                  Vergul bilan. TSD shu kodlar bo'yicha tovarni topadi.
                </span>
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {field("product_group", "Guruh kodi", "1C dagi kod. Nomini 1C beradi, bu yerda o'zgarmaydi.")}
                {field("name_ru", "Nomi (ruscha)")}
              </div>
              <div style={{ display: "flex", gap: 16 }}>
                <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                  <input type="checkbox" checked={form.fragile} onChange={set("fragile")} disabled={!canWrite} />
                  Mo'rt — palletda faqat yuqorida
                </label>
                <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                  <input type="checkbox" checked={form.no_stack} onChange={set("no_stack")} disabled={!canWrite} />
                  Ustiga yuk qo'yilmaydi
                </label>
              </div>
              {(packages.data?.items.length ?? 0) > 0 && (
                <>
                  <div className="sku-section-title">Qadoqlar</div>
                  <table className="data" style={{ width: "100%" }}>
                    <thead>
                      <tr>
                        <th>Qadoq</th>
                        <th style={{ textAlign: "right" }}>Birlikda</th>
                        <th>Shtrix-kod</th>
                      </tr>
                    </thead>
                    <tbody>
                      {packages.data?.items.map((pack) => (
                        <tr key={pack.id}>
                          <td>
                            <span className="mono">{pack.code}</span>
                            {pack.name ? ` · ${pack.name}` : ""}
                            {pack.is_base ? " · bazaviy" : ""}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            {qty(pack.units_per_pack)} {text(sku.base_uom)}
                          </td>
                          <td className="mono">{pack.barcodes?.join(", ") || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              {/* Ombordagi qoldiq MANZIL bo'yicha: «bu tovar
                  qayerda yotibdi» degan savolga karta javob
                  bermasa, xodim xaritaga yoki qoldiq hisobotiga
                  o'tib, o'sha tovarni qaytadan izlashi kerak
                  bo'lardi. */}
              <SkuStock skuID={sku.id} uom={sku.base_uom} />

              <SkuOXLimit skuID={sku.id} uom={sku.base_uom} />
            </div>
          )
        }
        onClose={() => {
          setEditing(null);
          setConflict(false);
          save.reset();
        }}
        onConfirm={() => {
          if (!canWrite) {
            setEditing(null);
            return;
          }
          if (!sku || !form) return;
          const changes = changesOf(formOf(sku), form);
          if (Object.keys(changes).length === 0) {
            setEditing(null);
            return;
          }
          setConflict(false);
          save.mutate(
            { sku, changes },
            {
              onSuccess: () => {
                setEditing(null);
                save.reset();
              },
              onError: (cause) => {
                // dev_spec §11.2: 409 da yangi ma'lumot ko'rsatiladi,
                // foydalanuvchi qarorisiz ustidan yozilmaydi.
                if (cause instanceof ApiError && cause.status === 409) {
                  setConflict(true);
                  save.reset();
                  void card.refetch();
                }
              },
            },
          );
        }}
      />
    </>
  );
}

/**
 * 1C bergan ma'lumot — o'qish uchun.
 *
 * Nega alohida blok: kartadagi maydonlarning yarmi 1C niki va
 * ularni WMS da o'zgartirib bo'lmaydi. Ularni tahrir maydonlari
 * bilan aralashtirib qo'yish xodimni 1C qiymatini WMS dan
 * tuzatmoqchi bo'lishga undardi — o'zgarish keyingi
 * sinxronizatsiyada baribir yo'qolardi.
 *
 * Yetishmagan qiymat «—» emas, OCHIQ yoziladi: «1C yubormadi».
 * Bo'sh chiziqcha «ma'lumot yo'q» bilan «1C bermadi» ni
 * farqlamasdi, holbuki ikkinchisi 1C tomonidagi ish.
 */
function OneCFacts({ sku }: { sku: Sku }) {
  const missing = <span className="sku-missing">1C yubormadi</span>;
  // Hisob rejimi 1C niki va WMS uni o'zgartirmaydi: partiya yoki
  // seriya hisobi qabulda nima so'ralishini belgilaydi.
  const policy = [
    sku.tracks_lot ? "partiya" : "",
    sku.tracks_serial ? "seriya" : "",
    sku.shelf_life_days ? `yaroqlilik ${sku.shelf_life_days} kun` : "",
  ].filter(Boolean);

  return (
    <div className="sku-facts">
      <div className="sku-photo">
        {sku.photo_url ? (
          <img src={sku.photo_url} alt={sku.name} loading="lazy" />
        ) : (
          <span className="sku-photo-empty">
            <Icon name="media" size={20} />
            Rasm yo'q
          </span>
        )}
      </div>

      <dl className="kv sku-kv">
        <dt>Guruh</dt>
        <dd>
          {sku.product_group_name || sku.product_group ? (
            <>
              {text(sku.product_group_name || sku.product_group)}
              {/* Kod ham qoladi: 1C bilan solishtirishda kerak. */}
              {sku.product_group_name && sku.product_group && (
                <span className="sku-sub mono">{sku.product_group}</span>
              )}
            </>
          ) : (
            missing
          )}
        </dd>

        <dt>Brend</dt>
        <dd>{sku.brand ? sku.brand : missing}</dd>

        <dt>Birlik</dt>
        {/* «dona» va «komplekt» bir xil tovar emas: qabulda
            miqdor aynan shu birlikda sanaladi. */}
        <dd>{text(sku.base_uom)}</dd>

        <dt>Qadoq</dt>
        {/* Nom oxiridagi «2x6»: 1C da alohida maydon yo'q. */}
        <dd>{sku.pack_format ? <span className="mono">{sku.pack_format}</span> : missing}</dd>

        <dt>Hisob</dt>
        <dd>{policy.length > 0 ? policy.join(" · ") : "oddiy — partiyasiz"}</dd>
      </dl>
    </div>
  );
}

/**
 * Tovar qaysi manzillarda yotibdi.
 *
 * Qoldiq hisobotidan (reporting) o'qiladi, master-data dan emas:
 * qoldiq inventarizatsiya o'lchovi va u xaritadagi raqam bilan
 * BIR XIL manbadan kelishi kerak — aks holda karta bir son,
 * xarita boshqa son ko'rsatardi.
 */
interface OXPolicy {
  id: string;
  location_id: string;
  location_code?: string;
  warehouse_code?: string;
  min_stock: string;
  max_stock: string;
}

/**
 * OX min/max (to'ldirish siyosati) — SKU ning OX katagidagi chegara.
 *
 * MAX terishda ham ishlatiladi: buyurtma MAX dan katta bo'lsa tovar OX
 * dan emas, to'g'ridan-to'g'ri palletdan (B, keyin C korpus) olinadi.
 */
function SkuOXLimit({ skuID, uom }: { skuID: string; uom?: string }) {
  const session = useSession();
  const canEdit = session.can(PERM.replenishmentPlan);
  const policies = useList<OXPolicy>("replenishment", "/api/v1/replenishment/policies", {
    sku_id: skuID,
    active_only: true,
    limit: 50,
  });
  const [form, setForm] = useState({ cell: "", min: "", max: "" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const rows = policies.data?.items ?? [];

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const cell = form.cell.trim().toUpperCase();
      const location = await request<{ id: string; code: string; warehouse_code?: string; location_function?: string }>(
        "topology",
        `/api/v1/locations/by-code/${encodeURIComponent(cell)}`,
      );
      if (location.location_function !== "OX") {
        throw new Error(`${cell} — OX katak emas`);
      }
      await request("replenishment", "/api/v1/replenishment/policies", {
        method: "POST",
        body: {
          sku_id: skuID,
          location_id: location.id,
          location_code: location.code,
          warehouse_code: location.warehouse_code,
          min_stock: form.min.trim() || "0",
          max_stock: form.max.trim(),
        },
      });
      setForm({ cell: "", min: "", max: "" });
      void policies.refetch();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="sku-section-title">
        OX min / max
        <span className="sku-sub">MAX dan katta buyurtma palletdan olinadi</span>
      </div>
      {rows.length > 0 && (
        <table className="data" style={{ width: "100%" }}>
          <thead>
            <tr>
              <th>OX katak</th>
              <th style={{ textAlign: "right" }}>Min</th>
              <th style={{ textAlign: "right" }}>Max</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td className="mono">{text(row.location_code)}</td>
                <td style={{ textAlign: "right" }}>{qty(row.min_stock)} {text(uom)}</td>
                <td style={{ textAlign: "right" }}>{qty(row.max_stock)} {text(uom)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {rows.length === 0 && !policies.isLoading && (
        <p className="field-hint" style={{ margin: 0 }}>
          Chegara yo'q — buyurtma OX da yetsa OX dan olinadi.
        </p>
      )}
      {canEdit && (
        <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr auto", gap: 8, alignItems: "end" }}>
          <label className="field">
            <span className="field-label">OX katak</span>
            <input className="input mono" placeholder="A01-01-01-03" value={form.cell}
              onChange={(event) => setForm({ ...form, cell: event.target.value })} />
          </label>
          <label className="field">
            <span className="field-label">Min</span>
            <input className="input mono" inputMode="decimal" value={form.min}
              onChange={(event) => setForm({ ...form, min: event.target.value })} />
          </label>
          <label className="field">
            <span className="field-label">Max</span>
            <input className="input mono" inputMode="decimal" value={form.max}
              onChange={(event) => setForm({ ...form, max: event.target.value })} />
          </label>
          <button type="button" className="btn btn-tonal" disabled={busy || !form.cell.trim() || !form.max.trim()}
            onClick={() => void save()}>
            Saqlash
          </button>
        </div>
      )}
      {error && (
        <div className="notice" role="alert">
          <Icon name="alert" size={16} />
          <span>{error}</span>
        </div>
      )}
    </>
  );
}

function SkuStock({ skuID, uom }: { skuID: string; uom?: string }) {
  const stock = useReport("stock", { sku_id: skuID, limit: 100 });
  const rows = stock.data?.items ?? [];

  const total = rows.reduce((sum, row) => sum + Number(row.on_hand_qty || 0), 0);
  const reserved = rows.reduce((sum, row) => sum + Number(row.reserved_qty || 0), 0);

  return (
    <>
      <div className="sku-section-title">
        Omborda
        {rows.length > 0 && (
          <span className="sku-sub">
            {total.toLocaleString("uz")} {text(uom)} · {rows.length} manzilda
            {reserved > 0 ? ` · ${reserved.toLocaleString("uz")} band` : ""}
          </span>
        )}
      </div>

      {stock.isLoading ? (
        <div className="skeleton" style={{ height: 72 }} />
      ) : rows.length === 0 ? (
        <p className="field-hint" style={{ margin: 0 }}>
          Bu tovar omborda yo'q — hech bir yacheykada qoldiq qolmagan.
        </p>
      ) : (
        <table className="data" style={{ width: "100%" }}>
          <thead>
            <tr>
              <th>Manzil</th>
              <th>Zona</th>
              <th>LP</th>
              <th style={{ textAlign: "right" }}>Qoldiq</th>
              <th style={{ textAlign: "right" }}>Band</th>
              <th style={{ textAlign: "right" }}>Mavjud</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={`${row.location_code}-${row.lp_id}-${index}`}>
                <td className="mono" style={{ whiteSpace: "nowrap" }}>
                  {row.location_code}
                  {row.blocked === "ha" && (
                    <>
                      {" "}
                      <StatusBadge value="blok" tone="error" />
                    </>
                  )}
                </td>
                <td>{text(row.location_function || row.zone_code)}</td>
                <td className="mono">{text(row.lp_id)}</td>
                <td style={{ textAlign: "right" }}>{qty(row.on_hand_qty)}</td>
                <td style={{ textAlign: "right" }}>{qty(row.reserved_qty)}</td>
                <td style={{ textAlign: "right" }}>{qty(row.available_qty)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {stock.data?.has_more && (
        <p className="field-hint" style={{ margin: 0 }}>
          Ro'yxat kesilgan: tovar 100 dan ortiq manzilda. To'lig'i «Qoldiq» bo'limida.
        </p>
      )}
    </>
  );
}
